import React, { useContext, useState, useCallback } from "react";
import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  Fade,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  downloadDocument,
  fetchDocumentForView,
} from "../../../data/statementsDocumentsAPI";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { determineRoleLabel } from "../../utils/msal-functions";
import {
  BORROWER,
  PAYMENTS_BORROWER_ROLE,
} from "../../constants/Roles.constants";

const FullScreenTransition = React.forwardRef(function FullScreenTransition(
  props,
  ref,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function loanIdForApi(activeLoan) {
  if (activeLoan == null) return activeLoan;
  if (typeof activeLoan === "object" && activeLoan && "loanId" in activeLoan) {
    return activeLoan.loanId;
  }
  return activeLoan;
}

const StatementsAndDocumentsCell = ({ cellAttribute, cellData, rowInfo }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
  const [viewerTitle, setViewerTitle] = useState("");
  const [viewerMime, setViewerMime] = useState("");
  const [viewerError, setViewerError] = useState(null);

  const { token, userRoles, visibleRole, activeLoan, impersonatedUser } =
    useContext(AuthContext);
  const role = determineRoleLabel(visibleRole);
  const isBorrower = role === BORROWER;

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerLoading(false);
    setViewerError(null);
    setViewerTitle("");
    setViewerMime("");
    setViewerBlobUrl((prev) => {
      if (prev) {
        try {
          window.URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return null;
    });
  }, []);

  const handleDownload = async (documentId) => {
    const loanId = loanIdForApi(activeLoan);
    if (loanId && loanId !== "no-loan") {
      await downloadDocument(token, loanId, documentId, impersonatedUser);
    }
  };

  const handleView = async (documentId) => {
    const loanId = loanIdForApi(activeLoan);
    if (!loanId || loanId === "no-loan") return;

    closePopper();
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerError(null);
    setViewerBlobUrl((prev) => {
      if (prev) {
        try {
          window.URL.revokeObjectURL(prev);
        } catch {
          /* ignore */
        }
      }
      return null;
    });

    try {
      const result = await fetchDocumentForView(
        token,
        loanId,
        documentId,
        impersonatedUser,
      );
      if (!result) {
        setViewerError("Document preview is not available in this environment.");
        return;
      }
      const url = window.URL.createObjectURL(result.blob);
      const mime =
        result.blob.type && result.blob.type !== "application/octet-stream"
          ? result.blob.type
          : "application/pdf";
      setViewerBlobUrl(url);
      setViewerMime(mime);
      setViewerTitle(result.filename?.trim() || "Document");
    } catch {
      setViewerError("Could not load the document. Please try again.");
    } finally {
      setViewerLoading(false);
    }
  };

  const openPopper = (e) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const closePopper = () => {
    setOpen(false);
  };

  const isPdfMime =
    viewerMime === "application/pdf" ||
    (viewerMime && viewerMime.endsWith("/pdf")) ||
    viewerMime === "";

  const renderInputItem = () => {
    if (
      cellAttribute === "actions" &&
      !impersonatedUser &&
      userRoles?.includes(PAYMENTS_BORROWER_ROLE)
    ) {
      return (
        <>
          <IconButton onClick={openPopper}>
            <MoreHorizIcon />
          </IconButton>
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper sx={{ paddingTop: 1, paddingBottom: 1 }}>
                  <ClickAwayListener onClickAway={closePopper}>
                    <Stack>
                      <MenuItem
                        disabled={!isBorrower}
                        onClick={() => handleView(rowInfo.documentId)}
                      >
                        <ListItemIcon>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View Document</ListItemText>
                      </MenuItem>
                      <MenuItem
                        disabled={!isBorrower}
                        onClick={() => handleDownload(rowInfo.documentId)}
                      >
                        <ListItemIcon>
                          <FileDownloadOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Download Document</ListItemText>
                      </MenuItem>
                    </Stack>
                  </ClickAwayListener>
                </Paper>
              </Fade>
            )}
          </Popper>

          <Dialog
            fullScreen
            open={viewerOpen}
            onClose={closeViewer}
            TransitionComponent={FullScreenTransition}
            aria-labelledby="document-viewer-title"
            PaperProps={{
              sx: {
                display: "flex",
                flexDirection: "column",
                height: "100%",
              },
            }}
          >
            <AppBar sx={{ position: "relative" }} color="default" elevation={1}>
              <Toolbar>
                <Typography
                  id="document-viewer-title"
                  sx={{ flex: 1, pr: 2 }}
                  variant="h6"
                  component="h2"
                  noWrap
                >
                  {viewerTitle}
                </Typography>
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={closeViewer}
                  aria-label="Close document viewer"
                >
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                bgcolor: "grey.200",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {viewerLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {viewerError && !viewerLoading && (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">{viewerError}</Alert>
                </Box>
              )}
              {!viewerLoading && !viewerError && viewerBlobUrl && isPdfMime && (
                <embed
                  title={viewerTitle}
                  src={viewerBlobUrl}
                  type={viewerMime || "application/pdf"}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              )}
              {!viewerLoading && !viewerError && viewerBlobUrl && !isPdfMime && (
                <iframe
                  title={viewerTitle}
                  src={viewerBlobUrl}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              )}
            </Box>
          </Dialog>
        </>
      );
    }
    return <>{cellData}</>;
  };
  return renderInputItem();
};

export default StatementsAndDocumentsCell;
